'use client';

import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { css, cx } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';
import { LucideIcon } from '@/src/components/LucideIcon';
import Image from 'next/image';
import { useStaticMapUrl } from '@/src/lib/hooks/query/useStaticMapUrl';
import {
  mapImageHidden,
  mapImageOverlay,
  mapImageVisible,
  miniMapContainer,
  pinOverlay,
  placeholderSurface,
} from './miniMap.styles';

type MiniMapProps = {
  lat: number;
  lon: number;
  size?: number;
};

const MINI_MAP_ZOOM = 13;

const isValidCoordinate = (lat: number, lon: number): boolean => {
  return (
    typeof lat === 'number' &&
    typeof lon === 'number' &&
    isFinite(lat) &&
    isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
};

const PlaceholderSvg = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="100%" height="100%" fill={token.var('colors.bg.softest')} />
    <g opacity="0.3">
      <line x1="0" y1="14" x2={size} y2="14" stroke={token.var('colors.border.neutral')} strokeWidth="2" />
      <line x1="0" y1="28" x2={size} y2="28" stroke={token.var('colors.border.neutral')} strokeWidth="1" />
      <line x1="14" y1="0" x2="14" y2={size} stroke={token.var('colors.border.neutral')} strokeWidth="2" />
      <line x1="30" y1="0" x2="30" y2={size} stroke={token.var('colors.border.neutral')} strokeWidth="1" />
    </g>
    <rect x="6" y="4" width="12" height="6" fill={token.var('colors.brand.primary')} opacity="0.7" rx="1" />
    <circle cx="32" cy="10" r="5" fill={token.var('colors.bg.softer')} opacity="0.7" />
  </svg>
);

// Lucide's MapPin is two elements: an outer pin path + an inner circle.
// Lucide React applies stroke="currentColor"/fill="none" uniformly, so we
// override per-element fills via CSS to get the multi-color treatment the
// static-map context needs (brand-teal pin with a white center dot, plus a
// thin white outline for contrast against varied map tile backgrounds).
const pinStyle = css({
  '& path': { fill: 'brand.primary' },
  '& circle': { fill: 'white' }, // tokens swept per HP-7690
});

const PinSvg = () => (
  // Pin foreground stays pure white in both themes.
  <LucideIcon icon={MapPin} size="sm" color={token.var('colors.white')} className={pinStyle} />
);

/**
 * MiniMap — small static map thumbnail for HALO HDP headers.
 *
 * Renders a signed Google Static Maps image tightly cropped around (lat, lon).
 * Legacy HALO fetched the signed URL via a Redux thunk (`StaticMap.generate()`);
 * we use the vercel `useStaticMapUrl` hook which hits the same backend signing
 * endpoint under React Query.
 *
 * While loading: flat neutral placeholder. Once the image decodes, a teal pin
 * overlays the center. On error: falls back to a decorative SVG placeholder.
 */
export const MiniMap = ({ lat, lon, size = 42 }: MiniMapProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  const validCoords = isValidCoordinate(lat, lon);

  const bounds = validCoords ? { minLat: lat, maxLat: lat, minLon: lon, maxLon: lon } : undefined;

  const { data: mapUrl, isError: queryError } = useStaticMapUrl({
    bounds,
    width: size,
    height: size,
    zoom: MINI_MAP_ZOOM,
  });

  const hasError = queryError || hasImageError;

  return (
    <div className={miniMapContainer} style={{ width: size, height: size }}>
      {!hasError && <div className={placeholderSurface} />}
      {hasError && <PlaceholderSvg size={size} />}
      {mapUrl && !hasError && (
        <Image
          src={mapUrl}
          alt="Map location"
          fill
          unoptimized
          className={cx(mapImageOverlay, imageLoaded ? mapImageVisible : mapImageHidden)}
          onLoad={() => setImageLoaded(true)}
          onError={() => setHasImageError(true)}
        />
      )}
      {validCoords && imageLoaded && !hasError && (
        <div className={pinOverlay}>
          <PinSvg />
        </div>
      )}
    </div>
  );
};
